import { useMemo, useState } from "react";
import moment from "moment";
import { useAppTranslation } from "../../../../../i18n/useAppTranslation";
import { uiNotification } from "../../../../../services/UINotificationService";
import {
  DOCUMENT_TYPES,
  usePatientDocumentsQuery,
} from "../../../../../api/patientDocuments/usePatientDocumentsQuery";
import { getPathParams } from "../utilities";
import { useDebouncedState } from "../../../../../utilities/hooks/useDebouncedState";
import { useEntityOrderState } from "../../../../../utilities/hooks/useEntityOrderState";

export function useDocuments() {
  var { tClients: t } = useAppTranslation.Clients();
  var { patientId } = getPathParams();
  var order = useEntityOrderState();
  var { 0: search, 1: debouncedSearch, 2: setSearch } = useDebouncedState("");

  var { 0: filter, 1: setFilter } = useState({
    type: "",
    expired: false,
  });

  var { 0: period, 1: setPeriod } = useState({
    from: moment().startOf("month").toDate(),
    to: new Date(),
  });

  var {
    data,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
  } = usePatientDocumentsQuery({
    payload: {
      pageSize: 15,
      patientId,
      searchTerm: debouncedSearch,
      period,
      filter,
      order: order.unwrap(),
    },
    options: {
      staleTime: 0,
      cacheTime: 0,
      keepPreviousData: true,
      onError: () => {
        uiNotification.error(t("documents.error.fetch"));
      },
    },
  });

  var documents = useMemo(
    () =>
      data?.pages
        ?.map((p) => p.documents)
        .reduce((d, p) => [...d, ...p], [])
        .map((x) => ({
          id: x.id,
          objectId: x.object_id,
          name: x.name,
          docType: x.type,
          isProviderQuestionnaire:
            x.type === DOCUMENT_TYPES.providerQuestionnaire,
          type: x.type_label,
          date: x.created_at,
          expiry: x.expiration_date,
          editor: x.created_by,
          isLock: x.is_lock,
          isExpired: Boolean(x.is_expired),
          expireSoon: x.expire_soon,
          url: x.url,
          acknowledgedAt: x.acknowledged_at,
          lockProcedureId: x.procedure_id,
          gfeProcedureId: x.object_id,
          isGfe: x.is_gfe,
          gfe: x.gfe,
          canEdit: x.can_edit,
          expirationDateEditor: x.expiration_date_editor,
          lastEditedExpirationDate: x.last_edited_expiration_date,
          expirationDate: x.expiration_date,
        })) || [],
    [data?.pages],
  );

  var docTypes = [
    {
      label: t("documents.filter.doc_type_all"),
      value: "",
    },
    ...(data?.pages?.[0]?.docTypes || []),
  ];

  var isSoftLoading =
    Object.values({
      ...filter,
      ...order,
      ...period,
    }).some(Boolean) &&
    isFetching &&
    !isLoading &&
    !isFetchingNextPage;

  return {
    documents: {
      value: documents,
      docTypes,
      hasMore: Boolean(hasNextPage),
      isNextFetching: isFetchingNextPage,
      isLoading: isLoading,
      fetchMore: fetchNextPage,
      refetch,
      isSoftLoading,
    },
    filter: {
      value: filter,
      update: setFilter,
    },
    order,
    period: {
      value: period,
      update: setPeriod,
    },
    search: {
      value: search,
      update: setSearch,
    },
  };
}
